import { onMounted, ref, watch } from 'vue';
import { selectionSorter } from '../../content';

/**
 * This function provides the drop down list to fill the page-specific items
 * @param {function,array} items An array or fn gives list of items
 * @param {string} contentType Current content type
 * @returns {array} lazyItems - Page-specific items, search - String empty
 * @example{
 *   const newsContentItems = () => {
 *     return [{id:1, value: "someValue1"},{id:2, value: "someValue2"}]
 *   };
 *
 *
 *   const contentType = "news"
 *
 *   const [lazyItems, search] = useAutocomplete(items, contentType)
 *
 *   return[
 *     lazyItems: [{id:1, title: "someTitle1"},{id:2, title: "someTitle2"}],
 *     search: ''
 *   ]
 * }
 */
export const useAutocomplete = (items, contentType) => {
  const lazyItems = ref([]);
  const search = ref('');

  if (typeof items === 'function') {
    onMounted(async () => {
      lazyItems.value = await items(contentType);
    });
  } else {
    lazyItems.value = items;
  }

  const clearSearch = () => {
    search.value = '';
  };

  return [
    lazyItems,
    search,
    clearSearch,
  ];
};

/**
 * This function sorts items again according to the selected items.Thus, the selected items are placed at the top of the dropdown list.
 * @param {array} items An array gives list of items
 * @param {array} value Selected items array
 * @param {function} filterFn A function that sorts selected items according to their value or id
 * @param {string} valueField Determines selected items according to which field to sort
 * @returns {array} sortedItems - Sorted items array
 * @example{
 *    const items = [{id:1, value: "someValue1"},{id:2, value: "someValue2"}];
 *
 *    const valueField = "id";
 *
 *    const selectedItems = [{id:2, value: "someValue2"}];
 *
 *    const [sortedItems] = useSelectedSorter(items,selectedItems,filterSelectedValues,valueField);
 *
 *    return sortedItems: [{id:2, value: "someValue2"},{id:1, value: "someValue1"}];
 * }
 */
export const useSelectedSorter = (items, value, filterFn, valueField) => {
  const sortedItems = ref([]);
  const sortItems = () => {
    const valueOfSortedItems = !value.value || Array.isArray(value.value) ? value.value : [value.value];

    sortedItems.value = selectionSorter(filterFn, valueField)(items.value, valueOfSortedItems);
  };

  watch(value, sortItems);
  watch(items, sortItems);

  return [
    sortedItems,
  ];
};
